if (!customElements.get('product-discount-price')) {
  customElements.define(
    'product-discount-price',
    class ProductDiscountPrice extends HTMLElement {
      constructor() {
        super();
        this.abortController = null;
        this.variantChangeUnsubscriber = null;
        this.productInfo = null;
      }

      connectedCallback() {
        this.productInfo = this.closest('product-info');
        if (!this.productInfo) return;

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId !== this.productInfo.sectionId) return;
          this.updateFromVariant(event.data.variant?.id);
        });

        const variantInput = this.productInfo.querySelector('input[name="id"]');
        if (variantInput?.value) {
          this.updateFromVariant(variantInput.value);
        }
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
        this.abortController?.abort();
      }

      get priceContainer() {
        return this.productInfo.querySelector(`#price-${this.productInfo.dataset.section}`);
      }

      updateFromVariant(variantId) {
        if (!variantId || !this.priceContainer) return;

        const priceElement = this.priceContainer.querySelector('.price');
        if (!priceElement) return;

        if (priceElement.classList.contains('price--on-sale') && !priceElement.dataset.jsDiscountApplied) {
          this.clearDiscountMessage();
          return;
        }

        this.abortController?.abort();
        this.abortController = new AbortController();
        const signal = this.abortController.signal;

        this.fetchDiscountedPrice(variantId, signal)
          .then((discountInfo) => {
            if (signal.aborted) return;
            this.applyDiscountDisplay(priceElement, discountInfo);
          })
          .catch(() => {
            if (signal.aborted) return;
          });
      }

      async fetchDiscountedPrice(variantId, signal) {
        const cartResponse = await fetch(`${window.routes.cart_url}.js`, { signal });
        const cart = await cartResponse.json();

        const existingItem = cart.items.find((item) => item.variant_id === Number(variantId));
        const previousQuantity = existingItem ? existingItem.quantity : 0;

        await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
          signal,
        });

        const updatedCart = await fetch(`${window.routes.cart_url}.js`, { signal }).then((response) =>
          response.json()
        );
        const lineItem = updatedCart.items.find((item) => item.variant_id === Number(variantId));

        if (!lineItem) return null;

        const discountInfo = {
          originalPrice: lineItem.original_price,
          finalPrice: lineItem.final_price,
          discounts: lineItem.line_level_discount_allocations || [],
          hasDiscount: lineItem.original_price > lineItem.final_price,
        };

        await fetch(window.routes.cart_change_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: lineItem.key, quantity: previousQuantity }),
          signal,
        });

        return discountInfo;
      }

      applyDiscountDisplay(priceElement, discountInfo) {
        if (!discountInfo?.hasDiscount) {
          if (priceElement.dataset.jsDiscountApplied) {
            delete priceElement.dataset.jsDiscountApplied;
          }
          this.clearDiscountMessage();
          return;
        }

        const discountPercent = Math.round(
          ((discountInfo.originalPrice - discountInfo.finalPrice) / discountInfo.originalPrice) * 100
        );

        priceElement.classList.add('price--on-sale', 'price--show-badge');
        priceElement.dataset.jsDiscountApplied = 'true';

        const saleContainer = priceElement.querySelector('.price__sale');
        const regularStrike = saleContainer?.querySelector('s.price-item--regular');
        const salePrice = saleContainer?.querySelector('.price-item--sale');

        if (regularStrike) {
          regularStrike.textContent = formatMoney(discountInfo.originalPrice, window.shopMoneyFormat);
        }
        if (salePrice) {
          salePrice.textContent = formatMoney(discountInfo.finalPrice, window.shopMoneyFormat);
        }

        const badge = priceElement.querySelector('.price__badge-sale');
        if (badge) {
          badge.textContent = `${discountPercent}% ${window.productDiscountStrings.onSale}`;
        }

        this.renderDiscountMessage(discountInfo.discounts);
      }

      renderDiscountMessage(discounts) {
        this.clearDiscountMessage();

        if (!discounts.length) return;

        const list = document.createElement('ul');
        list.className = 'discounts list-unstyled product__discount-list';
        list.setAttribute('role', 'list');

        discounts.forEach((allocation) => {
          const item = document.createElement('li');
          item.className = 'discounts__discount';
          item.textContent = allocation.discount_application?.title || '';
          list.appendChild(item);
        });

        this.appendChild(list);
      }

      clearDiscountMessage() {
        this.querySelector('.product__discount-list')?.remove();
      }
    }
  );
}

function formatMoney(cents, format) {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }

  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || '${{amount}}';
  const match = formatString.match(placeholderRegex);
  if (!match) return cents;

  function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
    if (isNaN(number) || number == null) return '0';
    const fixed = (number / 100.0).toFixed(precision);
    const parts = fixed.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    const centsPart = parts[1] ? decimal + parts[1] : '';
    return dollars + centsPart;
  }

  let value = '';
  switch (match[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_with_apostrophe_separator':
      value = formatWithDelimiters(cents, 2, "'", '.');
      break;
    case 'amount_no_decimals_with_space_separator':
      value = formatWithDelimiters(cents, 0, ' ');
      break;
    case 'amount_with_space_separator':
      value = formatWithDelimiters(cents, 2, ' ', ',');
      break;
    case 'amount_with_period_and_space_separator':
      value = formatWithDelimiters(cents, 2, ' ', '.');
      break;
    default:
      value = formatWithDelimiters(cents, 2);
  }

  return formatString.replace(placeholderRegex, value);
}
